/**
 * ::import widgets:
 * - PrettyImportWidget: L0 pretty widget for ::import{...} single-line directive
 * - ImportButtonGroupWidget: Run (sync) button for import directives
 */

import { WidgetType } from "@codemirror/view";
import { parseAssetRefPath } from "@magic-md/core";
import { t as translate } from "@magic-md/i18n";
import type { ExecutionStatus } from "../../../store/plugin-execution-store";
import {
    createClearExecutionButton,
    createPrettySyntaxShell,
    createSyntaxBadge,
    createTextSpan,
    createWatchIndicator,
    shieldGroupEvents,
} from "../shared/widgets";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

/* ── PrettyImportWidget — L0 pretty widget for ::import ── */

export class PrettyImportWidget extends WidgetType {
    constructor(
        readonly namespace: string,
        readonly source: string,
        readonly summary: string,
        readonly isWatched: boolean = false,
    ) {
        super();
    }
    eq(other: PrettyImportWidget) {
        return (
            other.namespace === this.namespace &&
            other.source === this.source &&
            other.summary === this.summary &&
            other.isWatched === this.isWatched
        );
    }
    toDOM(): HTMLElement {
        const icon = createSyntaxBadge("I");
        icon.classList.add("cm-magic-import-pretty-icon");

        return createPrettySyntaxShell({
            wrapperClass: "cm-magic-import-pretty",
            badge: icon,
            primary: [
                createTextSpan("cm-magic-import-pretty-ns", this.namespace),
                createTextSpan("cm-magic-import-pretty-arrow", "←"),
                createImportSourceElement(this.source),
                this.isWatched ? createWatchIndicator(this.namespace) : null,
            ].filter((item): item is HTMLElement => !!item),
            detail: null,
        });
    }
    ignoreEvent(): boolean {
        return false;
    }
}

function createImportSourceElement(source: string): HTMLElement {
    const assetRefMatch = source.match(/^\$ref\{([^}]*)\}$/);
    const assetRef = assetRefMatch ? parseAssetRefPath(assetRefMatch[1]) : null;
    if (!assetRefMatch) {
        return withImportSourceDataset(
            createTextSpan("cm-magic-import-pretty-source", source),
            source,
        );
    }
    if (!assetRef) {
        return withImportSourceDataset(
            createTextSpan("cm-magic-import-pretty-source", source),
            source,
        );
    }

    const assetId = assetRef.namespace ? `${assetRef.namespace}/assets` : "assets";
    const wrapper = document.createElement("span");
    wrapper.className =
        "cm-magic-import-pretty-source cm-magic-import-pretty-source-ref cm-magic-ref-pretty-source-asset";
    wrapper.title = source;

    const icon = createSyntaxBadge("r", { sub: "A" });
    icon.classList.add("cm-magic-ref-pretty-icon");

    wrapper.appendChild(icon);
    wrapper.appendChild(createTextSpan("cm-magic-ref-pretty-id", assetId));
    wrapper.appendChild(createTextSpan("cm-magic-ref-pretty-sep", "/"));
    wrapper.appendChild(createTextSpan("cm-magic-ref-pretty-path", assetRef.relativePath));
    return withImportSourceDataset(wrapper, source);
}

function withImportSourceDataset<T extends HTMLElement>(element: T, source: string): T {
    element.dataset.magicImportSource = source;
    element.dataset.magicNavKind = "import-source";
    return element;
}

/* ── ImportButtonGroupWidget — Run (sync) button for ::import ── */

export class ImportButtonGroupWidget extends WidgetType {
    constructor(
        readonly namespace: string,
        readonly source: string,
        readonly status: ExecutionStatus = "idle",
    ) {
        super();
    }

    eq(other: ImportButtonGroupWidget) {
        return (
            other.namespace === this.namespace &&
            other.source === this.source &&
            other.status === this.status
        );
    }

    toDOM(): HTMLElement {
        const group = document.createElement("span");
        group.className = "cm-magic-btn-group";

        // Prevent mouse events from reaching CodeMirror
        shieldGroupEvents(group);

        const isRunning = this.status === "running";
        const ns = this.namespace;
        const src = this.source;

        // Run/Stop button
        const runBtn = document.createElement("span");
        runBtn.className = isRunning
            ? "cm-magic-btn cm-magic-btn-stop"
            : "cm-magic-btn cm-magic-btn-run";
        runBtn.textContent = isRunning ? "■" : "▶";
        runBtn.title = isRunning
            ? tr("syntax.widget.import.stop", { namespace: this.namespace })
            : tr("syntax.widget.import.run", { namespace: this.namespace, source: this.source });
        runBtn.setAttribute(
            "aria-label",
            runBtn.title,
        );
        runBtn.addEventListener("mousedown", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isRunning) {
                window.dispatchEvent(
                    new CustomEvent("magic-md:cancel-import", {
                        detail: { namespace: ns },
                    }),
                );
            } else {
                window.dispatchEvent(
                    new CustomEvent("magic-md:run-import", {
                        detail: { namespace: ns, source: src },
                    }),
                );
            }
        });

        group.appendChild(runBtn);
        if (this.status === "error") {
            group.appendChild(
                createClearExecutionButton(
                    ns,
                    tr("syntax.widget.import.clearError", { namespace: this.namespace }),
                    tr("syntax.widget.import.clearError", { namespace: this.namespace }),
                ),
            );
        }
        return group;
    }

    ignoreEvent(): boolean {
        return true;
    }
}
